import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Orb } from "@/components/jarvis/Orb";

const inp = "mt-1 w-full border border-cyan-500/30 bg-slate-950 px-3 py-2.5 text-sm outline-none focus:border-cyan-400 transition-colors";
const btn = "w-full bg-cyan-400 py-3 font-hud text-sm font-bold tracking-[0.3em] text-slate-950 hover:bg-cyan-300 active:scale-[0.98] transition-transform disabled:opacity-60";

function F({ label, testId, ...p }) {
  return <label className="block"><span className="hud-label">{label}</span><input data-testid={testId} required className={inp} {...p} /></label>;
}

function SetupForm({ tokenConfigured, onDone }) {
  const [f, setF] = useState({ setup_token: "", email: "", name: "Santiago Pastorini", password: "", password2: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.password2) return setErr("Las contraseñas no coinciden.");
    setBusy(true); setErr("");
    try { const { password2, ...body } = f; await api.post("/auth/setup", body); onDone(); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="setup-form">
      <p className="text-sm text-slate-400">Primera instalación: cree la cuenta principal. Requiere el <b className="text-cyan-300">SETUP_TOKEN</b> definido en las variables del servidor.</p>
      {!tokenConfigured && <p className="text-sm text-amber-300" data-testid="setup-token-missing">El servidor no tiene SETUP_TOKEN configurado: agréguelo en Railway › Variables y reinicie.</p>}
      <F label="Token de instalación" testId="setup-token-input" type="password" value={f.setup_token} onChange={set("setup_token")} />
      <F label="Email" testId="setup-email-input" type="email" value={f.email} onChange={set("email")} />
      <F label="Nombre" testId="setup-name-input" value={f.name} onChange={set("name")} />
      <F label="Contraseña (mín. 10, letras y números)" testId="setup-password-input" type="password" value={f.password} onChange={set("password")} />
      <F label="Repetir contraseña" testId="setup-password2-input" type="password" value={f.password2} onChange={set("password2")} />
      {err && <p className="text-sm text-red-400" data-testid="setup-error">{err}</p>}
      <button data-testid="setup-submit-button" disabled={busy} className={btn}>{busy ? "CREANDO…" : "CREAR CUENTA"}</button>
    </form>
  );
}

export function ChangePassword() {
  const { refresh, logout } = useAuth();
  const [f, setF] = useState({ current_password: "", new_password: "", again: "" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.again) return setErr("Las contraseñas no coinciden.");
    try { await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password }); await refresh(); } catch (x) { setErr(errMsg(x)); }
  };
  return (
    <main className="min-h-screen flex items-center px-6 sm:px-16">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rise" data-testid="change-password-form">
        <p className="hud-label">Seguridad</p>
        <h1 className="font-hud text-3xl font-black text-cyan-300 glow-text">CAMBIO DE CONTRASEÑA OBLIGATORIO</h1>
        <p className="text-sm text-slate-400">Su cuenta usa una contraseña inicial. Defina una nueva para continuar, señor.</p>
        <F label="Contraseña actual" testId="cp-current-input" type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} />
        <F label="Nueva contraseña" testId="cp-new-input" type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} />
        <F label="Repetir nueva" testId="cp-again-input" type="password" value={f.again} onChange={(e) => setF({ ...f, again: e.target.value })} />
        {err && <p className="text-sm text-red-400" data-testid="cp-error">{err}</p>}
        <button data-testid="cp-submit-button" className={btn}>GUARDAR</button>
        <button type="button" onClick={logout} className="text-xs text-slate-500 hover:text-red-400" data-testid="cp-logout">Salir</button>
      </form>
    </main>
  );
}

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [setup, setSetup] = useState(null);
  const loadSetup = () => api.get("/auth/setup-status").then((r) => setSetup(r.data)).catch(() => setSetup({ needs_setup: false }));
  useEffect(() => { loadSetup(); }, []);
  if (user) return <Navigate to="/" replace />;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await login(email, password); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <main className="interface-login min-h-screen grid lg:grid-cols-2">
      <div className="login-reactor hidden lg:flex flex-col justify-center pl-20 gap-10">
        <Orb state={busy ? "pensando" : "inactivo"} size={340} />
        <p className="hud-label">TU MUNDO, EN SINCRONÍA</p>
      </div>
      <div className="login-content flex items-center px-6 sm:px-16 py-10">
        <div className="login-card w-full max-w-sm space-y-6 rise">
          <div className="lg:hidden flex justify-center"><Orb state="inactivo" size={160} /></div>
          <div>
            <p className="hud-label">Identificación requerida</p>
            <h1 className="font-hud text-4xl sm:text-5xl font-black tracking-tight text-cyan-300 glow-text mt-2">J.A.R.V.I.S.</h1>
          </div>
          {setup?.needs_setup ? <SetupForm tokenConfigured={setup.setup_token_configured} onDone={() => window.location.assign("/")} /> : (
            <form onSubmit={submit} className="space-y-6" data-testid="login-form">
              <p className="text-sm text-slate-400">Tu espacio personal te espera.</p>
              <F label="Email" testId="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <F label="Contraseña" testId="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
              {err && <p className="text-sm text-red-400" data-testid="login-error">{err}</p>}
              <button data-testid="login-submit-button" disabled={busy} className={btn}>{busy ? "VERIFICANDO…" : "ACCEDER"}</button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
